import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Card, Descriptions, Table, message, Spin } from 'antd';
import { jsPDF } from 'jspdf';
import { PrinterOutlined, ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';

interface Empresa {
  denominacion: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

interface Sucursal {
  direccion: string;
  localidad: string;
  provincia: string;
}

interface Balanza {
  id: number;
  numero_ticket: number;
  fecha_ticket: string;
  cliente_nombre: string;
  productor_nombre: string;
  kilos_primera: string;
  kilos_segunda: string;
  peso_neto: string;
  producto_nombre: string;
  establecimiento_nombre?: string;
  chofer_nombre?: string;
}

interface LiquidacionDetalle {
  id: number;
  balanza: Balanza;
  subtotal: string;
  alta: number;
}

interface LiquidacionFlete {
  id: number;
  numero_liquidacion: number;
  fecha_liquidacion: string;
  transportista: number;
  transportista_nombre: string;
  tipo_comprobante: string;
  numero_comprobante: string;
  subtotal: string;
  iva: string;
  otros_impuestos: string;
  alta: number;
  detalles: LiquidacionDetalle[];
  total: string;
}

interface DataState {
  empresa: Empresa;
  sucursal: Sucursal;
  liquidacion: LiquidacionFlete;
}

const ImprimirLiquidacionFletes: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<DataState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!id) {
      setError('ID de liquidación no proporcionado');
      return;
    }

    if (!user?.id_empresa || !user?.id_sucursal) {
      setError('No se encontraron datos de empresa o sucursal');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const empresa = user.id_empresa;
      const sucursal = user.id_sucursal;

      console.log('Fetching liquidation:', {
        empresa: user.id_empresa,
        sucursal: user.id_sucursal,
        id
      });

      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/imprimir_liquidacion_fletes/${empresa}/${sucursal}/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Verificar el tipo de contenido
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error(`Error en la respuesta del servidor: Tipo de contenido inválido ${contentType}`);
      }

      const jsonData = await response.json();
      console.log('Response:', jsonData);

      if (!response.ok) {
        throw new Error(jsonData.message || `Error del servidor: ${response.status} ${response.statusText}`);
      }

      if (jsonData.status === 'error') {
        throw new Error(jsonData.message || 'Error en la respuesta del servidor');
      }

      if (!jsonData.data) {
        throw new Error('No se recibieron datos de la liquidación');
      }

      // Validar la estructura de los datos
      if (!jsonData.data.empresa || !jsonData.data.sucursal || !jsonData.data.liquidacion) {
        throw new Error('La estructura de los datos recibidos no es válida');
      }

      setData(jsonData.data);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      const errorMessage = error.message || 'Error al obtener los datos de la liquidación';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id_empresa, user?.id_sucursal, getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatNumber = (value: string) => {
    return Number(value).toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const numeroALetras = (numero: string): string => {
    const value = Number(numero);
    const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const especiales = ['ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    if (value === 0) return 'CERO';

    const procesarNumero = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return unidades[n];
      if (n < 20) return especiales[n - 11];
      if (n < 100) {
        const decena = Math.floor(n / 10);
        const unidad = n % 10;
        return unidad === 0 ? decenas[decena] : `${decenas[decena]} Y ${unidades[unidad]}`;
      }
      if (n === 100) return 'CIEN';
      if (n < 1000) {
        const centena = Math.floor(n / 100);
        return `${centenas[centena]} ${procesarNumero(n % 100)}`.trim();
      }
      if (n < 1000000) {
        const miles = Math.floor(n / 1000);
        return `${miles === 1 ? 'MIL' : `${procesarNumero(miles)} MIL`} ${procesarNumero(n % 1000)}`.trim();
      }
      return 'NÚMERO DEMASIADO GRANDE';
    };

    const parteEntera = Math.floor(value);
    const centavos = Math.round((value - parteEntera) * 100);
    return `${procesarNumero(parteEntera)} CON ${centavos.toString().padStart(2, '0')}/100`;
  };

  const exportToPDF = useCallback(() => {
    if (!data) return;

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const leftMargin = 10;
      const rightMargin = pageWidth - 10;

      // Calcular espacios para orientación horizontal
      const headerHeight = 30;
      const tableHeaderHeight = 8;
      const tableRowHeight = 5;
      const maxTableRows = 20;
      const tableHeight = tableHeaderHeight + (tableRowHeight * maxTableRows);
      const footerHeight = 40;

      let y = 15;

      // Datos de la empresa (más compacto)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(data.empresa.denominacion, leftMargin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(`${data.empresa.direccion} - ${data.empresa.localidad} - ${data.empresa.provincia}`, leftMargin, y + 4);

      // Título y número
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('LIQUIDACIÓN DE FLETES', pageWidth / 2, y + 4, { align: 'center' });
      doc.text(`N° ${data.liquidacion.numero_liquidacion}`, rightMargin, y, { align: 'right' });
      doc.text(`Fecha: ${formatDate(data.liquidacion.fecha_liquidacion)}`, rightMargin, y + 4, { align: 'right' });

      // Datos del transportista y comprobante
      y += 10;
      doc.setFontSize(9);
      doc.text(`Transportista: ${data.liquidacion.transportista_nombre}`, leftMargin, y);
      doc.text(`Comprobante: ${data.liquidacion.tipo_comprobante} ${data.liquidacion.numero_comprobante}`, leftMargin, y + 4);

      // Inicio de la tabla
      y += 8;
      const tableTop = y;

      // Cabecera de la tabla con espaciado optimizado
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');

      // Posiciones de columnas ajustadas para formato horizontal
      const colTicket = leftMargin;
      const colFecha = leftMargin + 25;
      const colProductor = leftMargin + 50;
      const colEstablecimiento = leftMargin + 110;
      const colChofer = leftMargin + 170;
      const colProducto = leftMargin + 220;
      const colPesoNeto = rightMargin - 80;
      const colTarifa = rightMargin - 45;
      const colSubtotal = rightMargin - 10;

      // Cabeceras de columnas
      doc.text('Ticket', colTicket, y);
      doc.text('Fecha', colFecha, y);
      doc.text('Productor', colProductor, y);
      doc.text('Establecimiento', colEstablecimiento, y);
      doc.text('Chofer', colChofer, y);
      doc.text('Producto', colProducto, y);
      doc.text('Peso Neto', colPesoNeto, y, { align: 'right' });
      doc.text('Tarifa', colTarifa, y, { align: 'right' });
      doc.text('Subtotal', colSubtotal, y, { align: 'right' });

      // Variables para totales
      let totalPeso = 0;
      let totalImporte = 0;

      // Detalles de la tabla
      doc.setFont('helvetica', 'normal');
      data.liquidacion.detalles.forEach((detalle, index) => {
        if (index < maxTableRows) {
          y += tableRowHeight;
          doc.text(String(detalle.balanza.numero_ticket), colTicket, y);
          doc.text(formatDate(detalle.balanza.fecha_ticket), colFecha, y);
          doc.text(detalle.balanza.productor_nombre.substring(0, 25), colProductor, y);
          doc.text(detalle.balanza.establecimiento_nombre?.substring(0, 25) || '-', colEstablecimiento, y);
          doc.text(detalle.balanza.chofer_nombre?.substring(0, 20) || '-', colChofer, y);
          doc.text(detalle.balanza.producto_nombre?.substring(0, 20) || '-', colProducto, y);
          doc.text(formatNumber(detalle.balanza.peso_neto), colPesoNeto, y, { align: 'right' });
          doc.text(formatNumber(detalle.subtotal), colTarifa, y, { align: 'right' });

          const pesoNeto = Number(detalle.balanza.peso_neto);
          const tarifa = Number(detalle.subtotal);
          const subtotalCalculado = (pesoNeto * tarifa) / 1000;
          doc.text(formatNumber(String(subtotalCalculado)), colSubtotal, y, { align: 'right' });

          totalPeso += pesoNeto;
          totalImporte += subtotalCalculado;
        }
      });

      // Línea final de la tabla
      y = tableTop + tableHeight;
      doc.line(leftMargin, y, rightMargin, y);

      // Totales con alineación consistente
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('Totales:', colTicket, y);
      doc.text(formatNumber(String(totalPeso)), colPesoNeto, y, { align: 'right' });
      doc.text('-', colTarifa, y, { align: 'right' });
      doc.text(formatNumber(String(totalImporte)), colSubtotal, y, { align: 'right' });

      // Resumen final
      y += 8;
      doc.setFontSize(9);
      const columnWidth = 70;

      // Primera columna
      doc.text('Subtotal:', rightMargin - columnWidth, y);
      doc.text(formatNumber(data.liquidacion.subtotal), rightMargin - 10, y, { align: 'right' });
      y += 5;

      doc.text('IVA:', rightMargin - columnWidth, y);
      doc.text(formatNumber(data.liquidacion.iva), rightMargin - 10, y, { align: 'right' });
      y += 5;

      doc.text('Otros Impuestos:', rightMargin - columnWidth, y);
      doc.text(formatNumber(data.liquidacion.otros_impuestos), rightMargin - 10, y, { align: 'right' });
      y += 5;

      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL:', rightMargin - columnWidth, y);
      doc.text(formatNumber(data.liquidacion.total), rightMargin - 10, y, { align: 'right' });

      // Importe en letras
      y += 8;
      doc.setFont('helvetica', 'normal');
      doc.text('Son Pesos:', leftMargin, y);
      doc.text(numeroALetras(data.liquidacion.total), leftMargin + 25, y);

      // Guardar el PDF
      doc.save(`LF-${data.liquidacion.numero_liquidacion}.pdf`);
      message.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Error al generar el PDF');
    }
  }, [data]);

  const columns = [
    {
      title: 'Ticket',
      dataIndex: ['balanza', 'numero_ticket'],
      key: 'numero_ticket',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Fecha',
      dataIndex: ['balanza', 'fecha_ticket'],
      key: 'fecha_ticket',
      width: 120,
      render: (text: string) => formatDate(text),
    },
    {
      title: 'Productor',
      dataIndex: ['balanza', 'productor_nombre'],
      key: 'productor_nombre',
      ellipsis: true,
    },
    {
      title: 'Establecimiento',
      dataIndex: ['balanza', 'establecimiento_nombre'],
      key: 'establecimiento_nombre',
      ellipsis: true,
    },
    {
      title: 'Chofer',
      dataIndex: ['balanza', 'chofer_nombre'],
      key: 'chofer_nombre',
      ellipsis: true,
    },
    {
      title: 'Producto',
      dataIndex: ['balanza', 'producto_nombre'],
      key: 'producto_nombre',
      ellipsis: true,
    },
    {
      title: 'Peso Neto',
      dataIndex: ['balanza', 'peso_neto'],
      key: 'peso_neto',
      width: 120,
      align: 'right' as const,
      render: (value: string) => formatNumber(value),
    },
    {
      title: 'Tarifa',
      dataIndex: 'subtotal',
      key: 'tarifa',
      width: 120,
      align: 'right' as const,
      render: (value: string) => formatNumber(value),
    },
    {
      title: 'Subtotal',
      key: 'subtotal_calculado',
      width: 120,
      align: 'right' as const,
      render: (record: any) => {
        const pesoNeto = Number(record.balanza.peso_neto);
        const tarifa = Number(record.subtotal);
        const subtotal = (pesoNeto * tarifa) / 1000;
        return formatNumber(String(subtotal));
      },
    },
  ];

  const handleReturn = () => {
    navigate('/fletes_liquidaciones');
  };

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Card>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#ff4d4f' }}>Error</h2>
            <p>{error}</p>
            <Space size="middle">
              <Button icon={<ArrowLeftOutlined />} onClick={handleReturn}>
                Volver
              </Button>
              <Button type="primary" icon={<ReloadOutlined />} onClick={fetchData}>
                Reintentar
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {loading ? (
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '20px' }}>Cargando datos...</p>
        </div>
      ) : data ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#1890ff' }}>
                Liquidación de Fletes N° {data.liquidacion.numero_liquidacion}
              </h2>
            </div>

            <Descriptions bordered column={2}>
              <Descriptions.Item label="Empresa" span={2}>{data.empresa.denominacion}</Descriptions.Item>
              <Descriptions.Item label="Dirección">{data.empresa.direccion}</Descriptions.Item>
              <Descriptions.Item label="Localidad">{`${data.empresa.localidad} - ${data.empresa.provincia}`}</Descriptions.Item>
              <Descriptions.Item label="Fecha">{formatDate(data.liquidacion.fecha_liquidacion)}</Descriptions.Item>
              <Descriptions.Item label="Transportista">{data.liquidacion.transportista_nombre}</Descriptions.Item>
              <Descriptions.Item label="Comprobante">
                {`${data.liquidacion.tipo_comprobante} ${data.liquidacion.numero_comprobante}`}
              </Descriptions.Item>
            </Descriptions>

            <Table
              style={{ marginTop: '20px' }}
              columns={columns}
              dataSource={data.liquidacion.detalles}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1000, y: 400 }}
              summary={(pageData) => {
                let totalPeso = 0;
                let totalImporte = 0;

                pageData.forEach(({ balanza, subtotal }) => {
                  totalPeso += Number(balanza.peso_neto);
                  const pesoNeto = Number(balanza.peso_neto);
                  const tarifa = Number(subtotal);
                  const subtotalCalculado = (pesoNeto * tarifa) / 1000;
                  totalImporte += subtotalCalculado;
                });

                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <strong>Totales:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <strong>{formatNumber(String(totalPeso))}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <strong>-</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <strong>{formatNumber(String(totalImporte))}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />

            <Descriptions bordered column={1} style={{ marginTop: '20px' }}>
              <Descriptions.Item label="Subtotal">{formatNumber(data.liquidacion.subtotal)}</Descriptions.Item>
              <Descriptions.Item label="IVA">{formatNumber(data.liquidacion.iva)}</Descriptions.Item>
              <Descriptions.Item label="Otros Impuestos">{formatNumber(data.liquidacion.otros_impuestos)}</Descriptions.Item>
              <Descriptions.Item label="Total">
                <strong>{formatNumber(data.liquidacion.total)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Son Pesos">
                {numeroALetras(data.liquidacion.total)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <div style={{ textAlign: 'center' }}>
            <Space size="middle">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleReturn}
                size="large"
              >
                Volver
              </Button>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={exportToPDF}
                loading={loading}
                size="large"
              >
                Exportar a PDF
              </Button>
            </Space>
          </div>
        </Space>
      ) : null}
    </div>
  );
};

export default ImprimirLiquidacionFletes; 